import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import rugbyPitchImage from "../../assets/rugbyPitch.jpg";
import { DEFAULT_GRID_SIZE_METERS } from "./HeatMapSmoothing";

type HeatmapPoint = {
  x: number;
  y: number;
  value: number;
};

export type ScatterChartPoint = {
  x: number;
  y: number;
  size?: number;
  color?: string;
  label?: string;
  eventId?: number;
  symbol?: string;
  symbolRotate?: number;
};

type Props = {
  scatterPoints: ScatterChartPoint[];
  mode: "scatter" | "heatmap";
  heatmapPoints?: HeatmapPoint[];
  heatmapValueLabel?: string;
};

export const EChartsMetersGainedScatter: React.FC<Props> = ({
  scatterPoints,
  mode,
  heatmapPoints = [],
  heatmapValueLabel = "Value",
}) => {
  const option = useMemo(() => {
    if (mode === "heatmap") {
      const finitePoints = heatmapPoints.filter(
        (point) => Number.isFinite(point.x) && Number.isFinite(point.y)
      );

      if (!finitePoints.length) {
        return {
          backgroundColor: "transparent",
          tooltip: { show: false },
          grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: false },
          xAxis: { type: "category", data: [] as number[], show: false },
          yAxis: { type: "category", data: [] as number[], show: false },
          visualMap: [
            {
              min: 0,
              max: 1,
              calculable: true,
              inRange: {
                color: ["#1e88e5", "#66bb6a", "#ffee58", "#ef5350"],
              },
              show: false,
            },
          ],
          series: [
            {
              type: "heatmap",
              data: [],
              itemStyle: {
                opacity: 0.7,
              },
              emphasis: { focus: "none", scale: false },
            },
          ],
        };
      }

      const GRID_SIZE = 2; // must match DEFAULT_GRID_SIZE_METERS

      const xCategories = Array.from(
        { length: Math.ceil(120 / DEFAULT_GRID_SIZE_METERS) }, // number of columns
        (_, idx) => -10 + idx * DEFAULT_GRID_SIZE_METERS + DEFAULT_GRID_SIZE_METERS / 2
      );

      const yCategories = Array.from(
        { length: Math.ceil(70 / DEFAULT_GRID_SIZE_METERS) }, // number of rows if pitch width = 70
        (_, idx) => 0 + idx * DEFAULT_GRID_SIZE_METERS + DEFAULT_GRID_SIZE_METERS / 2
      );

      const seriesData = finitePoints.map((point) => {
        const xIndex = xCategories.indexOf(point.x);
        const yIndex = yCategories.indexOf(point.y);
        return {
          value: [xIndex, yIndex, point.value],
          label: `Pitch Location: (${point.x},${point.y})`, //m<br>${heatmapValueLabel}: ${point.value}
        };
      });

      const maxValue =
        seriesData.reduce((max, point) => Math.max(max, point.value?.[2] ?? 0), 0) || 1;

      return {
        backgroundColor: "transparent",
        tooltip: {
          trigger: "item",
          formatter: (params: any) => params?.data?.label || "",
          confine: true,
          valueFormatter: (val: any) => {
            const n = Number(val);
            return Number.isFinite(n) ? `${n.toFixed(1)}` : `${val}`;
          },
        },
        grid: { left: 25, right: 25, top: 23, bottom: 21, containLabel: false },
        xAxis: {
          type: "category",
          data: xCategories,
          show: false,
        },
        yAxis: {
          type: "category",
          data: yCategories,
          show: false,
        },
        visualMap: [
          {
            min: 0,
            max: maxValue,
            calculable: true,
            orient: "horizontal",
            left: "center",
            bottom: 20,
            show: false,
            inRange: {
              color: ["#1e88e5", "#66bb6a", "#ffee58", "#ef5350"],
            },
          },
        ],
        series: [
          {
            type: "heatmap",
            data: seriesData,
            blurSize: 30,
            pointSize: 20,
            itemStyle: {
              opacity: 0.7,
            },
            emphasis: { focus: "none", scale: false },
          },
        ],
      };
    }

    const finiteScatterPoints = (scatterPoints ?? []).filter(
      (point) => Number.isFinite(point.x) && Number.isFinite(point.y)
    );

    const points = finiteScatterPoints
      .map((point) => ({
        value: [point.x, point.y] as [number, number],
        size: point.size ?? 15,
        color: point.color ?? "#1976d2",
        label: point.label ?? "",
        eventId: point.eventId,
        symbol: point.symbol,
        symbolRotate: point.symbolRotate,
      }))
      .sort((a, b) => b.size - a.size)
      .map((point, idx, arr) => ({
        ...point,
        z: arr.length - idx,
        zlevel: 0,
      }));

    return {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        formatter: (params: any) => params?.data?.label || "",
        confine: true,
        valueFormatter: (val: any) => {
          const n = Number(val);
          return Number.isFinite(n) ? `${n.toFixed(1)}` : `${val}`;
        },
      },
      grid: { left: 8, right: 8, top: 8, bottom: 8, containLabel: false },
      xAxis: { type: "value", min: -14, max: 114, show: false },
      yAxis: { type: "value", min: -2, max: 72, show: false },
      series: [
        {
          type: "scatter",
          data: points,
          symbolSize: (val: any, params: any) => params.data.size,
          hoverAnimation: false,
          itemStyle: {
            color: (params: any) => params.data.color,
            borderColor: "#e0e0e0",
            borderWidth: 1,
          },
          emphasis: { focus: "none", scale: false, disabled: true, z: 0, zlevel: 0 },
        },
      ],
    };
  }, [heatmapPoints, heatmapValueLabel, mode, scatterPoints]);

  const onEvents = {
    click: (params: any) => {
      const id = params?.data?.eventId;
      if (id) {
        window.open(`${window.location.origin}/video-review?eventId=${id}`, "_blank");
      }
    },
  } as any;

  return (
    <div
      style={{
        width: "100%",
        height: 500,
        backgroundImage: `url(${rugbyPitchImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
      }}
    >
      <ReactECharts
        option={option}
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          zIndex: 1,
        }}
        onEvents={onEvents}
        notMerge
        lazyUpdate
      />
    </div>
  );
};
